import { retryFunc } from "@/api/retry";
import { Customer, MachineDetails, BomItem } from "@/client";
import {
  getCustomerByIdOptions,
  getMachineByIdOptions,
} from "@/client/@tanstack/react-query.gen";
import Bom, { BomLoading } from "@/components/bom";
import { MachineDetailsLayout } from "@/components/machine-details";
import { requireShopAuth } from "@/lib/shop-auth-guard";
import { Skeleton } from "@/components/ui/skeleton";
import { queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import placeholder from "@/assets/machine-placeholder.svg";
import { useCustomerId } from "@/components/customer-id-provider";
import { ErrorComponent } from "@/components/ui/ErrorComponent";

export type BomItemWithFlags = Omit<BomItem, "children" | "fullPosNr"> & {
  isMatch?: boolean;
  isForceExpanded?: boolean; // Automatically expand due to search results or selected descendant
  hasSelectedDescendant?: boolean;
  fullPosNr: string;
  uniqueId: string;
  children: BomItemWithFlags[];
};

function posNrSegment(posNo: number | null | undefined): string {
  return typeof posNo === "number" && !Number.isNaN(posNo) ? String(posNo) : "?";
}

function resolveFullPosNr(item: BomItem, parentFullPosNr?: string): string {
  if (item.fullPosNr != null && item.fullPosNr !== "") {
    return item.fullPosNr;
  }
  const local = posNrSegment(item.posNo);
  return parentFullPosNr != null && parentFullPosNr !== ""
    ? `${parentFullPosNr}.${local}`
    : local;
}

// Tree-only occurrence id: as_mi_id path from the root. Unique even when posNo collides.
function occurrenceId(asMiId: string, parentUniqueId?: string): string {
  return parentUniqueId ? `${parentUniqueId}::${asMiId}` : asMiId;
}

function initializeBomItem(
  items: BomItem[],
  parentFullPosNr?: string,
  parentUniqueId?: string
): BomItemWithFlags[] {
  return items.map((item) => {
    const fullPosNr = resolveFullPosNr(item, parentFullPosNr);
    const uniqueId = occurrenceId(item.as_mi_id, parentUniqueId);
    return {
      ...item,
      isMatch: false,
      isExpanded: false,
      fullPosNr,
      uniqueId,
      children: initializeBomItem(item.children ?? [], fullPosNr, uniqueId),
    };
  });
}

// Simple ErrorBoundary implementation if not using @sentry/react or react-error-boundary
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: unknown }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return <ErrorComponent error={this.state.error} />;
    }
    return this.props.children;
  }
}

export const Route = createFileRoute(
  "/customers/$customerId/machines/$machineId"
)({
  beforeLoad: requireShopAuth,
  loader: ({ params }) => {
    const { machineId, customerId } = params;
    queryClient.ensureQueryData({
      ...getMachineByIdOptions({
        path: { machine_id: machineId },
      }),
      retry: retryFunc,
    });
    queryClient.ensureQueryData({
      ...getCustomerByIdOptions({
        path: { customer_id: customerId },
      }),
      retry: retryFunc,
    });
  },
  component: () => (
    <ErrorBoundary>
      <React.Suspense
        fallback={<MachineDetailsLoadingPage></MachineDetailsLoadingPage>}
      >
        <MachineDetailsPage></MachineDetailsPage>
      </React.Suspense>
    </ErrorBoundary>
  ),
});

function MachineDetailsPage() {
  const { machineId, customerId } = Route.useParams();
  const { setCustomerId, customerId: contextCustomerId } = useCustomerId();
  const { data: machineData } = useSuspenseQuery({
    ...getMachineByIdOptions({
      path: { machine_id: machineId },
    }),
    retry: retryFunc,
  });

  const { data: customerData } = useSuspenseQuery({
    ...getCustomerByIdOptions({
      path: { customer_id: customerId },
    }),
    retry: retryFunc,
  });

  React.useEffect(() => {
    if (customerId && customerId !== contextCustomerId) {
      setCustomerId(customerId);
    }
  }, [customerId, contextCustomerId, setCustomerId]);

  const initializedBomItems = React.useMemo(
    () => initializeBomItem(machineData.bom ?? []),
    [machineData.bom]
  );

  return (
    <MachineDetailsLayout
      Image={<img src={machineData.previewFileInfo?.url ?? placeholder} className="w-full h-full object-contain" />}
      Bom={<Bom items={initializedBomItems} machineId={machineId}></Bom>}
      Details={
        <MachineDetailsText
          machine={machineData}
          customer={customerData}
        ></MachineDetailsText>
      }
    ></MachineDetailsLayout>
  );
}

function MachineDetailsLoadingPage() {
  return (
    <MachineDetailsLayout
      Image={<Skeleton className="w-full h-full"></Skeleton>}
      Bom={<BomLoading></BomLoading>}
      Details={<MachineDetailsTextSkeleton></MachineDetailsTextSkeleton>}
    ></MachineDetailsLayout>
  );
}

type detailsTextProps = {
  machine: MachineDetails;
  customer: Customer;
} & React.ComponentProps<"div">;

function MachineDetailsText({ machine, customer, ...props }: detailsTextProps) {
  return (
    <div
      {...props}
      className={cn("h-full flex flex-row gap-8 justify-start", props.className)}
    >
      <div  className="m-2">
        <h1 className="text-2xl font-bold">{machine.name}</h1>
        <h2 className="text-xl font-semibold">{machine.type}</h2>
      </div>
      <div className="m-2">
        <p className="font-bold">{customer.name}</p>
        <p className="font-normal">{customer.deliveryStreet}</p>
        <p className="font-normal">{customer.deliveryZip} {customer.deliveryCity}</p>
      </div>
    </div>
  );
}

function MachineDetailsTextSkeleton() {
  return <div></div>;
}
